import { removeCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import Link from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COOKIE_KEYS } from "@MasterBluePrint/Common/Utilities/CookieKeys";
import SignInForm from "@MasterBluePrint/Components/SignInForm";
import { GlobalAccountMenu } from "@MasterBluePrint/Widgets/GlobalAccountMenu";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface PageLinkModelCustom {
    title: string;
    url: string;
    type?: string;
    id: string;
    parentId?: string;
    excludeFromNavigation?: boolean;
    children?: PageLinkModelCustom[];
}
const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    firstName: state.context.session.firstName || state.context.session.email,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    globalMenuMapping = "globalMenuMapping",
    includeContactUsLink = "includeContactUsLink",
    contactUsLinkTitle = "contactUsLinkTitle",
    contactUsLinkUrl = "contactUsLinkUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.globalMenuMapping]: string;
        [fields.includeContactUsLink]: boolean;
        [fields.contactUsLinkTitle]: string;
        [fields.contactUsLinkUrl]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    wrapperDiv?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    titleTypographys?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    registerClickable?: InjectableCss;
    contactUsWrapper?: InjectableCss;
    contactTypography?: InjectableCss;
    titleClickables?: InjectableCss;
    orderStatusWrapper?: InjectableCss;
    orderStatusTypography?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 0;
            position: relative;
        `,
    },
    titleClickables: {
        css: css`
            &:focus:not(:focus-visible) {
                outline: none;
            }
            &:focus-visible {
                outline: 2px dashed #2060ad !important;
            }
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            padding-right: 1.5rem;

            max-width: 9.375rem;
            color: #535e64;
            font-family: "Roboto", sans-serif;
            font-size: .875rem;
            font-weight: bold;
            line-height: 1rem;
            text-align: right;
            &:hover {
                color: #2060ad;
                text-decoration: underline;
            }
        `,
        ellipsis: true,
        transform: "capitalize",
    },
    titleTypographys: {
        css: css`
            max-width: 9.375rem;
            color: #002554;
            font-family: "Roboto", sans-serif;
            font-size: .875rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: .9375rem;
            text-align: right;
            &:hover {
                color: #2060ad;
                text-decoration: underline;
            }
        `,
        ellipsis: true,
        transform: "capitalize",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 1.875rem;
            text-transform: capitalize;
            color: rgb(0, 37, 84);
            font-weight: bold;
            padding: 0 1.875rem 0 0;
            font-size: .875rem;
            &:hover {
                color: rgb(32, 96, 173);
                text-decoration: underline;
            }
        `,
    },
    wrapperDiv: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    registerClickable: {
        css: css``,
    },
    contactUsWrapper: {
        css: css`
            display: flex;
            text-transform: capitalize;
            background: rgba(0, 37, 84, 0.06);
            margin: 0;
            padding: 1.2rem 1.5rem;
            align-items: center;
            line-height: 1rem;
            &:empty {
                display: none;
            }
        `,
    },
    contactTypography: {
        css: css`
            margin: 0;
            max-width: 9.375rem;
            color: #535e64;
            font-family: "Roboto", sans-serif;
            font-size: .875rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1rem;
            white-space: nowrap;
            padding-right: .5rem;
            &:hover {
                color: #2060ad;
                text-decoration: underline;
            }
            &:focus:not(:focus-visible) {
                outline: none !important;
            }
            &:focus-visible {
                outline: 2px dashed #2060ad !important;
            }
        `,
    },
    orderStatusWrapper: {
        css: css`
            display: flex;
            text-transform: capitalize;
            margin: 0;
            padding:0;
            align-items: center;
            line-height: 1rem;
            &:empty {
                display: none;
            }
        `,
    },
    orderStatusTypography: {
        css: css`
            flex: 0 0 auto;
            text-transform: capitalize;
            color: #535e64;
            font-weight: bold;
            padding: 0 1.875rem 0 0;
            font-size: .875rem;
            &:hover {
                color: #2060ad;
                text-decoration: underline;
            }
            &:focus:not(:focus-visible) {
                outline: none !important;
            }
            &:focus-visible {
                outline: 2px dashed #2060ad !important;
            }
        `,
    },
};

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn: React.FC<Props> = ({
    firstName,
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    createAccountUrl,
}: Props) => {
    let mappingJson = "";
    const [show, setShow] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [myAccountPageLinks, setMyAccountPageLink] = useState(myAccountPageLink as PageLinkModelCustom);
    const [myAccountMapping, setMyAccountMapping] = useState(mappingJson);
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    const groups: string[] = [];

    useEffect(() => {
        if (typeof fields?.globalMenuMapping !== "undefined" && fields?.globalMenuMapping !== "") {
            mappingJson = JSON.parse(fields?.globalMenuMapping.replace(/\//g, ""));
        }
        setMyAccountPageLink(myAccountPageLink as PageLinkModelCustom);
        setMyAccountMapping(mappingJson);
        updateLinkGroups();
    }, [show, myAccountPageLink]);

    if (fields.icon === "User") {
        icon = User;
    }

    const updateLinkGroups = () => {
        Object.keys(myAccountMapping).forEach(function (group) {
            groups.push(group);
        });
    };

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const onSignUpHandler = () => {
        if (createAccountUrl) {
            history.push(createAccountUrl);
        }
    };
    const onSignOutHandler = () => {
        if(!isPunchOutSession) {
            removeCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_ACCEPTED);
            removeCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_POPUP_SHOWN);
        }
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {(!userName || currentUserIsGuest) && (
                <Clickable
                    {...styles.registerClickable}
                    onClick={onSignUpHandler}
                    data-test-selector="signInCreateNewAccount_createNewAccount"
                >
                    <Typography {...styles.titleTypography}>{translate("Register")}</Typography>
                </Clickable>
            )}

            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <>
                    <Clickable
                        {...styles.titleClickable}
                        onClick={() => setShow(!show)}
                        data-test-selector="header_signIn"
                    >
                        {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                        {showLabel && (
                            <Typography {...styles.titleTypographys} data-test-selector="header_userName">
                                Welcome {firstName}
                            </Typography>
                        )}
                    </Clickable>

                    <StyledWrapper {...styles.signOutWrapper}>
                        {show && (
                            <>
                                <StyledWrapper>
                                    <GlobalAccountMenu
                                        myAccountMapping={myAccountMapping}
                                        linkGroups={groups}
                                        firstName={firstName}
                                        myAccountPageLink={myAccountPageLinks}
                                        signOut={onSignOutHandler}
                                        isPunchOutSession={isPunchOutSession}
                                        cancelPunchOut={cancelPunchOut}
                                        show={show}
                                        setShow={setShow}
                                    />
                                </StyledWrapper>
                                <StyledWrapper
                                    // background overlay here
                                    id="overlayAccountMenu"
                                    style={{
                                        background: "#002554",
                                        opacity: 0.2,
                                        width: "100%",
                                        height: "100%",
                                        top: 0,
                                        left: 0,
                                        position: "fixed",
                                        zIndex: 2,
                                    }}
                                    onClick={() => setShow(false)}
                                ></StyledWrapper>
                            </>
                        )}
                    </StyledWrapper>
                </>
            )}
            {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <>
                    <Clickable {...styles.titleClickables} data-test-selector="header_signIn" role="link">
                        {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                        {showLabel && (
                            <Typography {...styles.titleTypography} onClick={() => setIsOpen(!isOpen)}>
                                {signInStatusText}
                            </Typography>
                        )}
                    </Clickable>
                    {isOpen && <SignInForm onClose={() => setIsOpen(false)} />}
                </>
            )}
            {userName && !currentUserIsGuest && (
                <Clickable
                    {...styles.signOutClickable}
                    onClick={onSignOutHandler}
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Clickable>
            )}

            {(!userName || currentUserIsGuest) && (
                <Clickable {...styles.orderStatusWrapper} href="/OrderStatus">
                    <Typography {...styles.orderStatusTypography}>Order Status</Typography>
                </Clickable>
            )}

            {(userName && !currentUserIsGuest) && (
                <Clickable {...styles.orderStatusWrapper} href="/MyAccount/Orders">
                    <Typography {...styles.orderStatusTypography}>Order History</Typography>
                </Clickable>
            )}

            <StyledWrapper {...styles.contactUsWrapper}>
                {fields.includeContactUsLink && (
                    <Link href={fields.contactUsLinkUrl} {...styles.contactTypography}>
                        <Typography {...styles.contactTypography}>{translate(fields.contactUsLinkTitle)}</Typography>
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.globalMenuMapping,
                displayName: "Global Menu Group-Link Mapping",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.includeContactUsLink,
                displayName: "Include Contact US Link?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.contactUsLinkTitle,
                displayName: "Contact Us Link Title",
                editorTemplate: "TextField",
                defaultValue: "Contact Us",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.includeContactUsLink,
            },
            {
                name: fields.contactUsLinkUrl,
                displayName: "Contact Us Link Url",
                editorTemplate: "TextField",
                defaultValue: "/contact-us",
                fieldType: "General",
                isVisible: widget => widget.fields.includeContactUsLink,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
